import { FC } from 'react';
import { navigate } from 'gatsby';
import { useQueryClient } from 'react-query';
import { DateTime } from 'luxon';
import merge from 'lodash/merge';
import { ButtonPrimary } from '@lambdacurry/component-library';
import {
  useMyPaymentMethodsQuery,
  useOrdersQuery,
  usePreviewDraftOrderQuery,
  useSubscriptionProductsQuery,
  useUpdateSubscriptionMutation
} from 'lib/home/generated/queries';
import { MeQuery, OrdersQuery } from 'lib/home/generated/types/api';
import { Card, CardSection, Grid } from 'lib/components';
import {
  selectCurrentOrderFromOrders,
  selectDefaultFromPaymentMethods,
  selectFrequencyFromMe
} from '../../../util/helpers';
import { DefaultLayout, Loading, CartPriceBreakdown, RedirectRouter } from '../../../components';
import { formatDate } from 'lib/util/date';

export interface ConfirmChangePlanProps {
  planId: string;
}

export interface ConfirmChangePlanPageProps {
  location?: { state: { planId: string } };
}

const ConfirmChangePlan: FC<ConfirmChangePlanProps> = ({ planId }) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isSuccess } = useUpdateSubscriptionMutation();
  const { data: subscriptionProducts } = useSubscriptionProductsQuery();
  const { data: orders } = useOrdersQuery();
  const { data: paymentMethods } = useMyPaymentMethodsQuery();
  const { data: draftOrder } = usePreviewDraftOrderQuery({
    input: { product_variant_id: planId }
  });

  if (!orders || !paymentMethods || !draftOrder || !subscriptionProducts) return <Loading />;

  const handleClick = () => {
    mutate(
      { updateUserSubscriptionInput: { product_variant_id: planId } },
      {
        onSuccess: ({ updateSubscription }) => {
          window.dataLayer.push({
            event: 'change-plan',
            current_plan: planId,
            previous_plan: orders.me.subscription.product_variant_id
          });

          queryClient.setQueryData(['Orders'], (query: OrdersQuery) => {
            query.me.subscription = merge({}, query.me.subscription, updateSubscription);

            return query;
          });

          navigate('/account');
        }
      }
    );
  };

  if (isLoading || isSuccess) return <Loading />;

  return (
    <DefaultLayout backTo="/account/change-plan">
      {({ me }: { me: MeQuery['me'] }) => {
        const frequency = selectFrequencyFromMe(me);
        const currentOrder = selectCurrentOrderFromOrders(orders);
        const defaultPaymentMethod = selectDefaultFromPaymentMethods(paymentMethods);
        const { cart } = draftOrder.previewDraftOrder;
        const billingDate = formatDate(DateTime.fromISO(currentOrder.complete_order_at), 'ff');

        return (
          <Grid className="items-start">
            <Card className="col-span-12 md:col-span-6 md:col-start-4 flex flex-col">
              <CardSection>
                <h3>Perfect, Quick Review!</h3>

                <p className="font-medium mb-0">
                  Charged <span className="uppercase">{defaultPaymentMethod.card.brand}</span>{' '}
                  {defaultPaymentMethod.card.last4}
                </p>

                <CartPriceBreakdown cart={cart} subscriptionFrequency={frequency} />

                <p className="bg-brand-green-50 my-6 p-4 text-xs">
                  Please note:
                  <br />
                  You will not be billed immediately. You will be billed on <strong>{billingDate}.</strong>
                </p>

                <ButtonPrimary className="w-full md:w-auto" onClick={handleClick}>
                  Confirm New Plan
                </ButtonPrimary>
              </CardSection>
            </Card>
          </Grid>
        );
      }}
    </DefaultLayout>
  );
};

const ConfirmChangePlanPage: FC<ConfirmChangePlanPageProps> = ({ location }) => (
  <RedirectRouter
    component={<ConfirmChangePlan planId={location?.state?.planId} />}
    location={location}
    to="/account/change-plan"
  />
);

export default ConfirmChangePlanPage;
